import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, LinearProgress, useTheme } from '@mui/material';
import { AccessTime, Schedule } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetClient } from '../redux/sagas/requests/client';
import { statusColor } from '../utils/statusColors';

const SummaryGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const SummaryCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.surface.inset,
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center',
}));

const SummaryValue = styled(Typography)(({ theme, color }) => ({
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.size.xlm,
    color: color || theme.palette.primary.main,
}));

const SummaryLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const TimeTypeSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const TimeTypeHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
});

const TimeTypeLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    fontWeight: theme.typography.fontWeightMedium,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
}));

const TimeTypeValue = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.text.secondary,
}));

const StyledProgress = styled(LinearProgress, { shouldForwardProp: (prop) => prop !== 'barColor' })(({ theme, barColor }) => ({
    height: 10,
    borderRadius: 5,
    backgroundColor: theme.palette.surface.track,
    ...(barColor && {
        '& .MuiLinearProgress-bar': {
            backgroundColor: barColor,
        },
    }),
}));

const TypeIcon = styled(Schedule, { shouldForwardProp: (prop) => prop !== 'iconColor' })(({ iconColor }) => ({
    color: iconColor,
}));

const TotalRow = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
}));

const TotalValue = styled(TimeTypeValue)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
}));

const EmptyIcon = styled(AccessTime)(({ theme }) => ({
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.5,
}));

const MonthLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const formatHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
};

const ClientTimeSummaryWidget = ({ widgetId, config = {}, onRemove }) => {
    const theme = useTheme();
    const clientId = useSelector(state => {
        const c = state.auth.currentUser?.client;
        return c?._id || c || null;
    });

    const { data: client, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetClient(clientId),
        cacheKey: `client-time-summary-${clientId}`,
        enabled: !!clientId,
    });

    // Get current month key (YYYYMM)
    const currentMonthKey = useMemo(() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        return `${year}${month}`;
    }, []);

    // Get current month name for display
    const currentMonthName = useMemo(() => {
        const now = new Date();
        return now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }, []);

    // Extract time data for the current month
    const monthlyTime = useMemo(() => {
        const aggregatedTime = client?.aggregatedTime?.monthly || {};
        const currentMonthData = aggregatedTime[currentMonthKey] || {};

        // Convert to array of { type, minutes }
        const timeEntries = Object.entries(currentMonthData)
            .filter(([type]) => type !== 'total')
            .map(([type, minutes]) => ({
                type,
                minutes: minutes || 0,
            }))
            .filter(entry => entry.minutes > 0)
            .sort((a, b) => b.minutes - a.minutes);

        return timeEntries;
    }, [client, currentMonthKey]);

    // Calculate totals
    const totalMinutes = monthlyTime.reduce((sum, entry) => sum + entry.minutes, 0);
    const retainerMinutes = monthlyTime.find(e => e.type.toLowerCase() === 'retainer')?.minutes || 0;
    const hourlyMinutes = monthlyTime.find(e => e.type.toLowerCase() === 'hourly')?.minutes || 0;

    // Get max minutes for progress bar scaling
    const maxMinutes = Math.max(...monthlyTime.map(e => e.minutes), 1);

    const hasData = monthlyTime.length > 0;

    return (
        <BaseWidget
            title="Monthly Time Summary"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            <MonthLabel>{currentMonthName}</MonthLabel>

            <SummaryGrid>
                <SummaryCard>
                    <SummaryValue color="#2196f3">{formatHours(retainerMinutes)}</SummaryValue>
                    <SummaryLabel>Retainer</SummaryLabel>
                </SummaryCard>
                <SummaryCard>
                    <SummaryValue color="#4caf50">{formatHours(hourlyMinutes)}</SummaryValue>
                    <SummaryLabel>Hourly</SummaryLabel>
                </SummaryCard>
            </SummaryGrid>

            {hasData ? (
                <>
                    {monthlyTime.map((entry, index) => (
                        <TimeTypeSection key={entry.type || index}>
                            <TimeTypeHeader>
                                <TimeTypeLabel>
                                    <TypeIcon
                                        fontSize="small"
                                        iconColor={statusColor(theme, 'billing', entry.type)}
                                    />
                                    {entry.type}
                                </TimeTypeLabel>
                                <TimeTypeValue>{formatHours(entry.minutes)}</TimeTypeValue>
                            </TimeTypeHeader>
                            <StyledProgress
                                variant="determinate"
                                value={(entry.minutes / maxMinutes) * 100}
                                barColor={statusColor(theme, 'billing', entry.type)}
                            />
                        </TimeTypeSection>
                    ))}

                    <TotalRow>
                        <TimeTypeHeader>
                            <TimeTypeLabel>
                                <AccessTime fontSize="small" />
                                Total Time
                            </TimeTypeLabel>
                            <TotalValue>
                                {formatHours(totalMinutes)}
                            </TotalValue>
                        </TimeTypeHeader>
                    </TotalRow>
                </>
            ) : (
                <EmptyState>
                    <EmptyIcon />
                    <Typography variant="body2">
                        No time recorded this month
                    </Typography>
                </EmptyState>
            )}
        </BaseWidget>
    );
};

export default ClientTimeSummaryWidget;
